import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import { ExternalVendor } from './ConnectionsController';

export type Event = {
  id: string;
  churchID: string;

  title: string;
  startsAt: string;
  endsAt: string;
  showTimes: boolean;

  description: string | null;
  location: string | null;
  registrationURL: string | null;
  thumbnailURL: string | null;

  waivers: Waiver[];
}

export type Waiver = {
  id: string;
  name: string;
  url: string;
  externalMetadata: WaiverExternalMetadata | null;
  required: boolean;
};

export type WaiverExternalMetadata = {
  externalID: string;
  source: 'SMART_WAIVER';
};

export type EventsResponse = {
  events: Event[];
}

export type ExternalEvent = {
  provider: ExternalVendor;
  externalID: string;
  name: string;
  description: string | null;
  registrationURL: string | null;
  thumbnailImageURL: string | null;
}

export type ExternalEventsResponse = {
  externalEvents: ExternalEvent[];
}

export type ExternalEventDetails = {
  planningCenter?: {
    selectionTypes: Array<{
      id: string;
      name: string;
    }>
  };
}

export type ExternalEventMetadataPlanningCenterMetdata = {
  signupID: string;
  selectionTypeMap: Record<string, string>;
}

export type EventAttendeeRole = 'standard' | 'volunteer' | 'admin';

export type EventAttendee = {
  id: string;
  churchID: string;
  eventID: string;
  name: string;
  birthday: string | null;
  grade: number | null;
  gender: string | null;

  emergencyContact: {
    name: string;
    phoneNumber: string;
  } | null

  registrant: {
    name: string;
    phoneNumber: string;
    email: string;
  } | null;

  notes: string | null;
  medicalNotes: string | null;
  group: string | null;
  saltUserID: string | null;
  role: EventAttendeeRole;

  externalSource: {
    externalID: string;
    sourceName: string;
  } | null;

  lastUpdatedAt: string | null;
  createdAt: string | null;

  completedWaivers: CompletedWaiver[];
}

export type CompletedWaiver = {
  id: string;
}

export type ExternalWaiver = {
  name: string;
  source: ExternalVendor;
  externalID: string;
  url: string;
}

export default class EventsController {
  private static baseURL: string = process.env.REACT_APP_EVENTS_BACKEND_URL || "";

  static async fetchEvents(churchID: string, auth: AuthContextType): Promise<HTTPResponse<EventsResponse>> {
    return await NetworkController.performRequestAt<EventsResponse>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events`),
      auth.consumeRefreshToken
    );
  }

  static async createEvent(
    churchID: string,
    body: {
      event: Event,
      planningCenterMetadata: ExternalEventMetadataPlanningCenterMetdata | null,
    },
    auth: AuthContextType
  ): Promise<HTTPResponse<Event>> {
    let payload = {
      eventAttributes: { ...body.event },
      planningCenterMetadata: null as ExternalEventMetadataPlanningCenterMetdata | null // this is a dumb typescript thing
    };

    if (body.planningCenterMetadata) {
      payload.planningCenterMetadata = body.planningCenterMetadata
    }

    return await NetworkController.performRequestAt<Event>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/church/${churchID}/events`, payload),
      auth.consumeRefreshToken
    );
  }

  static async fetchExternalImportOptions(churchID: string, provider: ExternalVendor, auth: AuthContextType): Promise<HTTPResponse<ExternalEventsResponse>> {
    return await NetworkController.performRequestAt<ExternalEventsResponse>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events/external-import-options/${provider}`),
      auth.consumeRefreshToken
    );
  }

  static async fetchExternalEventDetails(churchID: string, provider: string, externalEventID: string, auth: AuthContextType): Promise<HTTPResponse<ExternalEventDetails>> {
    return await NetworkController.performRequestAt<ExternalEventDetails>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events/external-import-options/${provider}/${externalEventID}`),
      auth.consumeRefreshToken
    );
  }

  static async fetchEvent(churchID: string, eventID: string, auth: AuthContextType): Promise<HTTPResponse<{ event: Event }>> {
    return await NetworkController.performRequestAt<{ event: Event }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events/${eventID}`),
      auth.consumeRefreshToken
    );
  }

  static async fetchAttendeesForEvent(churchID: string, eventID: string, auth: AuthContextType): Promise<HTTPResponse<{ attendees: EventAttendee[] }>> {
    return await NetworkController.performRequestAt<{ attendees: EventAttendee[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events/${eventID}/attendees`),
      auth.consumeRefreshToken
    );
  }

  static async fetchAvailableExternalWaivers(churchID: string, provider: ExternalVendor, auth: AuthContextType): Promise<HTTPResponse<{ externalWaivers: ExternalWaiver[] }>> {
    return await NetworkController.performRequestAt<{ externalWaivers: ExternalWaiver[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/church/${churchID}/events/available-waivers/${provider}`),
      auth.consumeRefreshToken
    );
  }

  static async attachExternalWaiverToEvent(
    churchID: String,
    eventID: String,
    externalWaiver: ExternalWaiver,
    instructions: string | null,
    auth: AuthContextType
  ): Promise<HTTPResponse<{ event: Event }>> {
    return await NetworkController.performRequestAt(
      this.baseURL,
      new HTTPRequest(HTTPMethod.PUT, `/church/${churchID}/events/${eventID}/waivers/external`, {
        externalWaiver,
        instructions
      }),
      auth.consumeRefreshToken
    )
  }

  static async createManualWaiverForEvent(
    churchID: String,
    eventID: String,
    waiver: {
      name: string,
      instructions: string | null,
      url: string
    },
    auth: AuthContextType
  ): Promise<HTTPResponse<{ event: Event }>> {
    return await NetworkController.performRequestAt(
      this.baseURL,
      new HTTPRequest(HTTPMethod.PUT, `/church/${churchID}/events/${eventID}/waivers`, waiver),
      auth.consumeRefreshToken
    )
  }

  static async sendWaiverReminderEmails(churchID: string, eventID: string, auth: AuthContextType): Promise<HTTPResponse<{ totalSent: number, totalFailed: number }>> {
    return await NetworkController.performRequestAt(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/church/${churchID}/events/${eventID}/actions/send-waiver-reminder-emails`),
      auth.consumeRefreshToken
    )
  }

  static async submitAttendeeWaiverCompletions(
    churchID: string,
    eventID: string,
    waiverID: string,
    markAsComplete: EventAttendee[],
    auth: AuthContextType
  ): Promise<HTTPResponse<{ attendees: EventAttendee[] }>> {
    return await NetworkController.performRequestAt(
      this.baseURL,
      new HTTPRequest(
        HTTPMethod.POST, 
        `/church/${churchID}/events/${eventID}/waivers/${waiverID}/actions/mark-attendees-complete`,
        {
          attendees: markAsComplete.map(attendee => attendee.id)
        }
      ),
      auth.consumeRefreshToken,
    )
  }
}
